import {
  isIndependentUser as sharedIsIndependentUser,
  isMember as sharedIsMember,
  isProvider as sharedIsProvider,
} from '@shared/rbac/member-roles'
import type { ChatUser } from '../types/chat'

type RoleUser = Pick<ChatUser, 'roleType'>

export type RoleAccent = 'member' | 'provider' | 'independent'

export const memberGreen = {
  badge:
    'inline-flex rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold leading-tight text-emerald-700',
  avatarBg: 'bg-emerald-600',
  avatarRing: 'ring-2 ring-emerald-400/50 shadow-sm shadow-emerald-200',
  rowBorder: 'border-l-emerald-500',
  rowSelected: 'border-l-emerald-600 bg-emerald-50/80',
  rowHover: 'hover:bg-emerald-50/50',
  rowWash: 'bg-emerald-50/40',
} as const

export const providerPurple = {
  badge:
    'inline-flex rounded-full bg-violet-50 px-2 py-0.5 text-[10px] font-semibold leading-tight text-violet-700',
  avatarBg: 'bg-brand-600',
  avatarRing: 'ring-2 ring-brand-500 shadow-sm shadow-violet-200/80',
  rowBorder: 'border-l-brand-600',
  rowSelected: 'border-l-brand-600 bg-brand-50/80',
  rowHover: 'hover:bg-brand-50/50',
  rowWash: 'bg-violet-50/40',
} as const

/** Distinct from Member emerald / Provider violet — Independent (AHP contacts). */
export const independentAmber = {
  badge:
    'inline-flex rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-semibold leading-tight text-amber-800',
  avatarBg: 'bg-amber-600',
  avatarRing: 'ring-2 ring-amber-400/60 shadow-sm shadow-amber-200',
  rowBorder: 'border-l-amber-500',
  rowSelected: 'border-l-amber-600 bg-amber-50/80',
  rowHover: 'hover:bg-amber-50/50',
  rowWash: 'bg-amber-50/40',
} as const

const ACCENT_BADGE: Record<RoleAccent, string> = {
  member: memberGreen.badge,
  provider: providerPurple.badge,
  independent: independentAmber.badge,
}

export function isMember(user: RoleUser): boolean {
  return sharedIsMember(user)
}

export function isProvider(user: RoleUser): boolean {
  return sharedIsProvider(user)
}

export function isIndependentUser(user: RoleUser): boolean {
  return sharedIsIndependentUser(user)
}

export function getRoleAccent(user: RoleUser): RoleAccent {
  if (isIndependentUser(user)) return 'independent'
  if (isProvider(user)) return 'provider'
  return 'member'
}

export function getUserRoleBadge(user: RoleUser): {
  label: 'Member' | 'Provider' | 'Independent'
  className: string
} | null {
  const role = (user.roleType ?? '').trim()
  if (!role) return null
  if (isIndependentUser(user)) {
    return { label: 'Independent', className: independentAmber.badge }
  }
  if (isProvider(user)) {
    return { label: 'Provider', className: providerPurple.badge }
  }
  return { label: 'Member', className: memberGreen.badge }
}

/** Job title when set; otherwise the role badge (Provider / Member / Independent). */
export function getPeerRoleLine(
  user: RoleUser & { title?: string | null },
): { label: string; className: string } | null {
  const title = user.title?.trim()
  if (title) {
    return { label: title, className: ACCENT_BADGE[getRoleAccent(user)] }
  }
  return getUserRoleBadge(user)
}

export function groupHasMembers(
  participantIds: string[],
  usersById: Map<string, RoleUser>,
): boolean {
  for (const id of participantIds) {
    const user = usersById.get(id)
    if (user && isMember(user)) return true
  }
  return false
}

export function getGroupAccent(
  participantIds: string[],
  usersById: Map<string, RoleUser>,
): RoleAccent {
  return groupHasMembers(participantIds, usersById) ? 'member' : 'provider'
}

export function getGroupParticipantSummary(
  participantIds: string[],
  usersById: Map<string, RoleUser>,
): string {
  let providers = 0
  let members = 0
  let independents = 0
  for (const id of participantIds) {
    const user = usersById.get(id)
    if (!user) continue
    if (isIndependentUser(user)) independents += 1
    else if (isProvider(user)) providers += 1
    else if (isMember(user)) members += 1
  }
  const parts: string[] = []
  if (providers > 0) parts.push(`${providers} provider${providers === 1 ? '' : 's'}`)
  if (members > 0) parts.push(`${members} member${members === 1 ? '' : 's'}`)
  if (independents > 0) {
    parts.push(`${independents} independent${independents === 1 ? '' : 's'}`)
  }
  return parts.join(' · ') || 'No participants'
}
